import fs from 'node:fs';
import path from 'node:path';
import { profilesDir } from '../profiles.js';
import { compareSemver } from '../version.js';

/**
 * Throttled "is a newer ghosty CLI published?" check. The cache file under
 * ~/.ghosty is the only state; reads are synchronous (cheap, done before a
 * command runs), the registry refresh is async and happens after a command
 * finishes — at most once per 24h, never in CI.
 */

export interface VersionCheckCache {
  /** ISO timestamp of the last registry check attempt (success or not). */
  checkedAt: string;
  /** Newest published version seen the last time a check succeeded. */
  latest?: string;
}

export const CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;

const REGISTRY_LATEST_URL = 'https://registry.npmjs.org/ghosty-cli/latest';
const FETCH_TIMEOUT_MS = 3000;

export function versionCheckPath(homedir?: string): string {
  return path.join(profilesDir(homedir), 'version-check.json');
}

/** Pipelines pin versions deliberately — never phone home or nag from CI. */
export function updateCheckDisabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return Boolean(env.CI || env.GHOSTY_NO_UPDATE_CHECK);
}

export function readCheckCache(homedir?: string): VersionCheckCache | null {
  try {
    const parsed = JSON.parse(
      fs.readFileSync(versionCheckPath(homedir), 'utf8'),
    ) as Partial<VersionCheckCache> | null;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.checkedAt !== 'string') return null;
    return {
      checkedAt: parsed.checkedAt,
      latest: typeof parsed.latest === 'string' ? parsed.latest : undefined,
    };
  } catch {
    return null;
  }
}

export function writeCheckCache(cache: VersionCheckCache, homedir?: string): void {
  fs.mkdirSync(profilesDir(homedir), { recursive: true, mode: 0o700 });
  const file = versionCheckPath(homedir);
  fs.writeFileSync(file, `${JSON.stringify(cache, null, 2)}\n`);
  try {
    fs.chmodSync(file, 0o600);
  } catch {
    /* Windows / exotic filesystems: skip silently */
  }
}

/** True when the last check is missing, corrupt, or older than 24h. */
export function isCheckDue(cache: VersionCheckCache | null, now: Date = new Date()): boolean {
  if (!cache) return true;
  const checkedAt = Date.parse(cache.checkedAt);
  if (Number.isNaN(checkedAt)) return true;
  return now.getTime() - checkedAt > CHECK_INTERVAL_MS;
}

/** The update the cache says is available, or null when current is newest. */
export function pendingUpdate(
  cache: VersionCheckCache | null,
  current: string,
): { current: string; latest: string } | null {
  if (!cache?.latest) return null;
  return compareSemver(cache.latest, current) === 1 ? { current, latest: cache.latest } : null;
}

/**
 * Refresh the cache from the registry, at most once per 24h. Network problems
 * are silent, but checkedAt is still stamped so a flaky connection doesn't
 * retry on every command; a previously seen `latest` is kept until a check
 * succeeds again.
 */
export async function refreshCheckCache(homedir?: string): Promise<void> {
  if (updateCheckDisabled()) return;
  const previous = readCheckCache(homedir);
  if (!isCheckDue(previous)) return;
  const cache: VersionCheckCache = {
    checkedAt: new Date().toISOString(),
    latest: previous?.latest,
  };
  try {
    const res = await fetch(REGISTRY_LATEST_URL, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) });
    if (res.ok) {
      const body = (await res.json()) as { version?: unknown };
      if (typeof body?.version === 'string') cache.latest = body.version;
    }
  } catch {
    /* offline or registry hiccup — the stamped checkedAt prevents retry storms */
  }
  writeCheckCache(cache, homedir);
}
